import { useEffect, useMemo, useRef } from 'react'

import type { SourceAnchor } from '@/services/ingestion'
import { findMatches, segmentLine } from './sourceSearch'

/** Locate the anchor's line in text: snippet first (robust across
 * region-relative line numbers), then the stated line number. */
function anchorLineIndex(lines: string[], anchor?: SourceAnchor | null): number | null {
  if (!anchor) return null
  const probe = (anchor.snippet ?? '').trim().slice(0, 60)
  if (probe.length >= 4) {
    const index = lines.findIndex((line) => line.includes(probe))
    if (index >= 0) return index
    const shorter = probe.slice(0, 24)
    if (shorter.length >= 4) {
      const shorterIndex = lines.findIndex((line) => line.includes(shorter))
      if (shorterIndex >= 0) return shorterIndex
    }
  }
  if (anchor.lineNo && anchor.lineNo >= 1 && anchor.lineNo <= lines.length) {
    return anchor.lineNo - 1
  }
  return null
}

export function HighlightedText({
  text,
  anchor,
  pageStartLines,
  mono,
  onDesignateLine,
  query,
  activeMatch = 0,
  onMatchCount,
}: {
  text: string
  anchor?: SourceAnchor | null
  pageStartLines?: number[]
  mono?: boolean
  /** Designate mode reports a coordinate; it never interprets the line. */
  onDesignateLine?: (lineNo: number, text: string) => void
  /** Find-in-document text. Every occurrence is marked; one is active. */
  query?: string
  activeMatch?: number
  /** How many occurrences this text holds, so the toolbar can count them. */
  onMatchCount?: (count: number) => void
}) {
  const lines = useMemo(() => text.split('\n'), [text])
  const highlightedIndex = useMemo(() => anchorLineIndex(lines, anchor), [lines, anchor])
  const highlightedRef = useRef<HTMLDivElement | null>(null)
  const activeRef = useRef<HTMLElement | null>(null)

  const matches = useMemo(() => findMatches(lines, query ?? ''), [lines, query])
  // The count the toolbar shows and the marks below come from ONE search, so
  // they cannot disagree about what a match is.
  useEffect(() => {
    onMatchCount?.(matches.length)
  }, [matches.length, onMatchCount])

  // How many matches precede each line, so a line can name its hits by their
  // index in the whole document.
  const firstMatchOnLine = useMemo(() => {
    const offsets = new Array<number>(lines.length).fill(0)
    let running = 0
    for (let i = 0; i < lines.length; i += 1) {
      offsets[i] = running
      running += matches.reduce((n, m) => (m.line === i ? n + 1 : n), 0)
    }
    return offsets
  }, [lines, matches])

  useEffect(() => {
    highlightedRef.current?.scrollIntoView?.({ block: 'center' })
  }, [highlightedIndex])

  // Follow the active hit as the operator steps through them.
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: 'center' })
  }, [activeMatch, matches.length])

  const pageAt = (lineIndex: number): number | null => {
    if (!pageStartLines?.length) return null
    const position = pageStartLines.indexOf(lineIndex + 1)
    return position >= 0 ? position + 1 : null
  }

  return (
    <div className={`text-[12px] leading-relaxed text-stone-200 ${mono ? 'font-mono' : 'font-sans'}`}>
      {lines.map((line, index) => {
        const page = pageAt(index)
        const highlighted = index === highlightedIndex
        return (
          <div key={index} ref={highlighted ? highlightedRef : undefined}>
            {page !== null && page > 1 && (
              <div className="my-2 flex items-center gap-2 select-none">
                <span className="h-px flex-1 bg-stone-800" />
                <span className="text-[9px] uppercase tracking-wider text-stone-600">Page {page}</span>
                <span className="h-px flex-1 bg-stone-800" />
              </div>
            )}
            <div
              data-testid={highlighted ? 'source-anchor-highlight' : undefined}
              className={[
                highlighted
                  ? 'bg-amber-400/20 border-l-2 border-amber-400 pl-2 -ml-2 rounded-sm whitespace-pre-wrap'
                  : 'whitespace-pre-wrap',
                onDesignateLine && line.trim()
                  ? 'rounded-sm hover:bg-amber-400/10 hover:ring-1 hover:ring-amber-400/40'
                  : '',
              ].join(' ')}
              onClick={onDesignateLine && line.trim()
                ? () => onDesignateLine(index + 1, line)
                : undefined}
              style={onDesignateLine && line.trim() ? { cursor: 'crosshair' } : undefined}
            >
              {query
                ? segmentLine(line, query, firstMatchOnLine[index] ?? 0).map((segment, si) =>
                    segment.hit ? (
                      <mark
                        key={si}
                        ref={segment.index === activeMatch ? activeRef : undefined}
                        data-testid={
                          segment.index === activeMatch ? 'source-search-active' : 'source-search-hit'
                        }
                        className={
                          segment.index === activeMatch
                            ? 'bg-amber-300 text-black rounded-sm'
                            : 'bg-amber-400/25 text-stone-100 rounded-sm'
                        }
                      >
                        {segment.text}
                      </mark>
                    ) : (
                      <span key={si}>{segment.text}</span>
                    ),
                  )
                : line || ' '}
            </div>
          </div>
        )
      })}
    </div>
  )
}
